import { useState } from 'react'
import {
  AlertIcon,
  CheckIcon,
  DashButton,
  DataSurface,
  FIELD,
  PageHead,
  Panel,
  StatusPill,
  TableCard,
  Td,
  Thead,
  Tr,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import {
  getClassPlayCode,
  getPlayAccess,
  groupCode,
  rotateClassPlayCode,
  type PlayAccessRow,
} from '../../../shared/api/play'
import { setPlayerRollNumber } from '../../../shared/api/student'
import type { StaffAuth } from '../../../shared/api/teaching'
import { launchPlayAs } from './playLaunch'
import { TEACHER_COPY, describeError, type Lang } from './copy'

/**
 * The screen a teacher opens to get a class playing.
 *
 * 🔴 THE "PLAY" DOOR USED TO LEAD TO A DISABLED BUTTON. `CHOOSE_PLAY` opens D2
 * (pick a child) → D3 (confirm) → a launch that is deliberately switched off,
 * because Teacher Play Mode needs a change to the gameplay write path. Two
 * screens to be told no. Meanwhile the thing a teacher actually needs to get a
 * class playing — the class code the children type — existed only in the
 * console output of a dev seed script, and `play-credentials.controller.ts` had
 * been sitting there scoped, tested and called by nothing.
 *
 * 🔴 THE CODE IS AUTO-GENERATED AND NOBODY CHOOSES IT, WHICH IS DELIBERATE AND
 * WORTH DEFENDING ON THIS SCREEN. It is minted on the first request and stable
 * after, from an alphabet with no `O`/`0`, `I`/`1`, `L` or `S`/`5`: a
 * five-year-old reads it off a whiteboard, and every one of those pairs is one
 * glyph to them. A teacher-chosen code would be `LKG2026` — memorable, and
 * unreadable to the person who has to type it. Codes are also globally unique,
 * so a school picking its own would collide with another school's.
 *
 * ⚠️ SO THE ONLY VERB OFFERED IS ROTATE, and it is behind a confirmation. That
 * is the one decision a school genuinely owns: a code that has walked out of the
 * building. Rotating mid-morning strands every tablet in the room until somebody
 * writes the new one up, which is why the button asks twice.
 *
 * 🔴 AND THE ROLL NUMBERS ARE EDITABLE HERE, WHICH IS NOT A CONVENIENCE. The
 * roll number is what `POST /auth/kid` resolves a child BY, so a typo on the
 * register is a child who cannot sign in — and until `PATCH /players/:id/roll`
 * existed, the only way to fix one was to move the child out of the class and
 * back in, or to have an admin re-import the whole spreadsheet. It belongs on
 * this screen rather than on the roster because this is where a teacher is
 * standing when they discover it.
 */

export interface PlaySetupScreenProps {
  classId: string
  lang?: Lang
  auth?: StaffAuth
  /** The old Play branch — D2's pick-a-child, still reachable, still disabled at D3. */
  onPlayAsChild?: () => void
}

export function PlaySetupScreen({
  classId,
  lang = 'en',
  auth = {},
  onPlayAsChild,
}: PlaySetupScreenProps) {
  const t = TEACHER_COPY[lang]

  /*
   * ⚠️ A COUNTER RATHER THAN A `reload()`. `useResource` is deliberately not a
   * cache and exposes only `retry`, which is the error path's button — bumping a
   * dependency is how this file asks for a fresh read after a write, and it is
   * the same shape the hook's own key comment describes.
   */
  const [rosterVersion, setRosterVersion] = useState(0)

  /*
   * ⚠️ TWO RESOURCES RATHER THAN ONE, because they fail independently and mean
   * different things. A class with no code yet still has a register worth
   * fixing, and a roster that 403s says nothing about the code.
   */
  const code = useResource<{ classId: string; code: string }>(
    (signal) => getClassPlayCode(classId, { ...auth, signal }),
    [classId],
  )
  const access = useResource<PlayAccessRow[]>(
    (signal) => getPlayAccess(classId, { ...auth, signal }),
    [classId, rosterVersion],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const [rotating, setRotating] = useState(false)
  const [confirmRotate, setConfirmRotate] = useState(false)
  const [rotated, setRotated] = useState<string | null>(null)
  const [rotateError, setRotateError] = useState<string | null>(null)

  async function rotate(): Promise<void> {
    setRotating(true)
    setRotateError(null)
    try {
      const next = await rotateClassPlayCode(classId, auth)
      /*
       * ⚠️ THE NEW CODE IS HELD LOCALLY RATHER THAN REFETCHED. The response IS
       * the new code, and a second GET to display what was just returned is a
       * round trip during which the panel would show the old one.
       */
      setRotated(next.code)
      setConfirmRotate(false)
    } catch (err) {
      setRotateError(describeError(asError(err), lang))
    } finally {
      setRotating(false)
    }
  }

  const shown = rotated ?? code.data?.code ?? null

  return (
    <section className="mx-auto max-w-5xl pb-12 font-dash text-dash-sm text-ink">
      <PageHead title={t.playSetupTitle} subtitle={t.playSetupHint} />

      {/* ── the code ─────────────────────────────────────────────────── */}
      <DataSurface
        state={code.state}
        errorMessage={describeError(code.error, lang)}
        errorRetryLabel={t.retry}
        onRetry={code.retry}
      >
        {shown === null ? null : (
          <Panel>
            <p className="font-dash text-dash-xs font-bold text-muted">{t.classCode}</p>

            {/*
              ⚠️ TABULAR AND WIDE-TRACKED, because this is transcribed by hand
              onto a board and then typed by a child. Proportional figures put a
              `1` at a third of the width of an `8`, which is exactly the
              difference somebody copying it will lose.
            */}
            <p
              data-testid="play-code"
              className="tabular mt-2 font-kid text-kid-lg font-extrabold tracking-[0.16em] text-ink"
            >
              {groupCode(shown)}
            </p>

            <p className="mt-3 max-w-[60ch] font-dash text-dash-xs text-muted">{t.classCodeWhy}</p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {confirmRotate ? (
                <>
                  {/*
                    🔴 THE CONFIRMATION NAMES THE CONSEQUENCE, NOT THE ACTION.
                    "Are you sure?" tells a teacher nothing; "every tablet using
                    the old code stops working" is the fact they need, and it is
                    the one they will not have thought of at 9:05.
                  */}
                  <p className="w-full max-w-[60ch] font-dash text-dash-xs font-bold text-ink">
                    {t.rotateConfirm}
                  </p>
                  <DashButton onClick={() => void rotate()} disabled={rotating}>
                    {rotating ? t.rotating : t.rotateYes}
                  </DashButton>
                  <DashButton
                    variant="secondary"
                    onClick={() => {
                      setConfirmRotate(false)
                    }}
                  >
                    {t.cancel}
                  </DashButton>
                </>
              ) : (
                <DashButton
                  variant="secondary"
                  onClick={() => {
                    setConfirmRotate(true)
                  }}
                >
                  {t.rotate}
                </DashButton>
              )}

              {onPlayAsChild ? (
                <DashButton variant="ghost" onClick={onPlayAsChild}>
                  {t.playAsChild}
                </DashButton>
              ) : null}
            </div>

            {rotateError !== null ? (
              <p role="alert" className="mt-4">
                <StatusPill tone="danger" icon={<AlertIcon size={14} />}>
                  {rotateError}
                </StatusPill>
              </p>
            ) : null}
          </Panel>
        )}
      </DataSurface>

      {/* ── the register ─────────────────────────────────────────────── */}
      {/*
        🔴 WHAT THE PLAY BUTTON ON EACH ROW ACTUALLY DOES, SAID ONCE ABOVE THE
        TABLE RATHER THAN THIRTY TIMES INSIDE IT. The consequence a teacher needs
        before the first tap is that the laptop leaves their hands and the round
        lands on that child's roll number — not on whoever used the tablet last,
        which is the thing this whole feature exists to stop being true.
      */}
      <p className="mt-8 max-w-[70ch] font-dash text-dash-xs text-muted">{t.playNowHint}</p>

      <div className="mt-4">
        <DataSurface
          state={access.state}
          skeletonRows={6}
          emptyMessage={t.noChildren}
          errorMessage={describeError(access.error, lang)}
          errorRetryLabel={t.retry}
          onRetry={access.retry}
        >
          {access.data === null ? null : (
            <TableCard>
              <table className="w-full border-collapse text-left">
                <Thead headers={[t.roll, t.student, t.signsInWith, '']} />
                <tbody>
                  {access.data.map((row) => (
                    <AccessRow
                      key={row.playerId}
                      row={row}
                      lang={lang}
                      auth={auth}
                      onSaved={() => {
                        setRosterVersion((v) => v + 1)
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </TableCard>
          )}
        </DataSurface>
      </div>
    </section>
  )
}

/**
 * `unknown` → the `Error | null` `describeError` is typed for.
 *
 * ⚠️ IT PRESERVES THE STATUS, which is the only thing `describeError` reads. A
 * `new Error(String(err))` here would turn every 401 into "couldn't load this"
 * and send whoever is testing the pilot hunting a network fault.
 */
function asError(err: unknown): Error | null {
  return err instanceof Error ? err : null
}

/**
 * One child: their roll number, editable, and which door they use.
 *
 * 🔴 THE ROW OWNS ITS OWN DRAFT AND ITS OWN ERROR. A single "editing" field on
 * the screen would make a 409 on roll 03 render beside roll 12 the moment a
 * teacher moved on — and a collision message that names the wrong child is worse
 * than none.
 */
function AccessRow({
  row,
  lang,
  auth,
  onSaved,
}: {
  row: PlayAccessRow
  lang: Lang
  auth: StaffAuth
  onSaved: () => void
}) {
  const t = TEACHER_COPY[lang]
  const [draft, setDraft] = useState(row.rollNumber ?? '')
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  /* Teacher Play Mode — confirm, then hand the laptop over. */
  const [confirming, setConfirming] = useState(false)
  const [launching, setLaunching] = useState(false)

  const dirty = draft.trim() !== (row.rollNumber ?? '')

  /**
   * 🔴 THE ROLL NUMBER IS NOT REQUIRED FOR THIS, AND THAT IS NOT AN OVERSIGHT.
   * A child signs *themselves* in by class code plus roll number, so a missing
   * roll locks them out — which is why this screen makes it editable. A teacher
   * launching for them names the child by `playerId`, so a child whose roll has
   * not been entered yet can still play this morning while the register is being
   * fixed. The confirmation says which case they are in.
   */
  async function play(): Promise<void> {
    setLaunching(true)
    setProblem(null)
    try {
      await launchPlayAs({ playerId: row.playerId, gameId: null, auth, returnTo: 'teacher' })
      /*
       * ⚠️ NO SUCCESS STATE. A launch that worked replaces this whole screen
       * with the child's app inside the same tick, so anything set here would be
       * drawn on a component that is already gone — and React would warn about
       * exactly that.
       */
    } catch (err) {
      setConfirming(false)
      setProblem(describeError(asError(err), lang))
    } finally {
      setLaunching(false)
    }
  }

  async function save(): Promise<void> {
    setSaving(true)
    setProblem(null)
    setSaved(false)
    try {
      await setPlayerRollNumber(row.playerId, draft.trim() === '' ? null : draft.trim(), auth)
      setSaved(true)
      onSaved()
    } catch (err) {
      /*
       * 🔴 A 409 CARRIES THE ONLY SENTENCE THAT HELPS, AND IT NAMES THE CHILD IN
       * THE WAY. `describeError` would flatten it to "couldn't load this" — so
       * the server's own message is preferred when there is one, which is the
       * one place on the teacher tier that is true.
       */
      const message = err instanceof Error && err.message !== '' ? err.message : null
      setProblem(message ?? describeError(asError(err), lang))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Tr>
      <Td>
        <label className="sr-only" htmlFor={`roll-${row.playerId}`}>
          {t.roll}
        </label>
        <input
          id={`roll-${row.playerId}`}
          data-testid={`roll-input-${row.playerId}`}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value)
            setSaved(false)
          }}
          /* ⚠️ `text`, NOT `number`: "03" keeps its zero and "3A" is a real roll
             number `play.prisma` types as a string precisely to keep. */
          inputMode="numeric"
          autoComplete="off"
          maxLength={16}
          className={`${FIELD} tabular w-24`}
        />
      </Td>

      <Td>
        <span className="font-bold text-ink">{row.name}</span>
      </Td>

      <Td>
        {/*
          🔴 THREE STATES, AND THE MIDDLE ONE IS NEW. A null `passwordHash` used
          to mean "cannot sign in"; since `kid-auth` gained the name door it
          means "types their own name", which is the ordinary arrangement for
          this age band. A screen written against the old meaning would tell a
          teacher their whole class was locked out.

          ⚠️ ICON + COLOUR + TEXT, never colour alone (design-system.md §2).
        */}
        {row.rollNumber === null ? (
          <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
            {t.needsRoll}
          </StatusPill>
        ) : row.hasPassword ? (
          <StatusPill tone="neutral" icon={<CheckIcon size={14} />}>
            {t.usesPassword}
          </StatusPill>
        ) : (
          <StatusPill tone="success" icon={<CheckIcon size={14} />}>
            {t.usesName}
          </StatusPill>
        )}
      </Td>

      <Td>
        <div className="flex flex-wrap items-center gap-2">
          <DashButton
            variant="secondary"
            disabled={!dirty || saving}
            onClick={() => void save()}
          >
            {saving ? t.saving : t.save}
          </DashButton>
          {saved && !dirty ? (
            <span className="font-dash text-dash-xs font-bold text-success">{t.saved}</span>
          ) : null}

          {/*
            🔴 THE LAUNCH, ON THE ROW WHERE THE TEACHER IS ALREADY STANDING.
            D2 → D3 is the deliberate, confirmed path — pick a child, pick a
            game, read the line that names both. This is the same act one tap
            deep, for the teacher who is going down a register handing the
            laptop to one child after another, and it deliberately picks NO game:
            the child's own home screen is a better chooser than a dropdown in a
            table cell.

            ⚠️ IT IS STILL CONFIRMED. Handing over the laptop ends whatever the
            teacher was doing on it, and an accidental tap in a dense table is
            exactly the thing the confirm exists for — the same argument the
            rotate button one panel up makes about a code on a whiteboard.
          */}
          {!confirming ? (
            <DashButton variant="ghost" disabled={launching} onClick={() => { setConfirming(true) }}>
              {launching ? t.launching : t.playNow}
            </DashButton>
          ) : null}
        </div>

        {confirming ? (
          <div className="mt-2 max-w-[46ch]">
            {/* 🔴 NAMES THE CHILD AND THE ROLL NUMBER — `launchConfirmLine`'s
                rule on D3, and it matters more here because this launch has no
                screen of its own in front of it. */}
            <p className="font-dash text-dash-xs font-bold text-ink">
              {t.playConfirm(row.name, row.rollNumber)}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <DashButton disabled={launching} onClick={() => void play()}>
                {launching ? t.launching : t.playConfirmYes}
              </DashButton>
              <DashButton
                variant="secondary"
                disabled={launching}
                onClick={() => { setConfirming(false) }}
              >
                {t.cancel}
              </DashButton>
            </div>
          </div>
        ) : null}

        {problem !== null ? (
          <p role="alert" className="mt-2 max-w-[42ch] font-dash text-dash-xs font-bold text-pill-danger-fg">
            {problem}
          </p>
        ) : null}
      </Td>
    </Tr>
  )
}
